import { useId, useMemo } from "react"

import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import { cn } from "@/clientWeb/components/lib/utils"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { DatePicker } from "@/clientWeb/components/ui/date-picker"
import { Label } from "@/clientWeb/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/clientWeb/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/clientWeb/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/clientWeb/components/ui/tabs"
import type { RecurrenceEndType, RecurringFrequency } from "../../models/meeting.model"
import {
  RECURRENCE_FREQUENCIES,
  RECURRENCE_MAX_INTERVAL,
  RECURRENCE_MAX_OCCURRENCES,
  END_DATE_PROBLEM_KEY,
  describeRecurrence,
  firstMeetingDay,
  latestEndDate,
  plural,
  recurrenceEndDateProblem,
  recurrenceToConfig,
  switchFrequency,
  toggleWeekDay,
  type RecurrenceState,
} from "../../utils/recurrence"
import { startOfDay } from "../../utils/schedule-time"

const KEY = "meetings.schedule.recurrence"

const FREQUENCY_LABEL: Record<RecurringFrequency, string> = {
  daily: "meetings.schedule.daily",
  weekly: "meetings.schedule.weekly",
  monthly: "meetings.schedule.monthly",
}

const INTERVAL_KEY: Record<RecurringFrequency, string> = {
  daily: "intervalDay",
  weekly: "intervalWeek",
  monthly: "intervalMonth",
}

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const

const range = (n: number) => Array.from({ length: n }, (_, i) => i + 1)

/** Trigger styling shared by every dropdown in the picker; the default fill, like the rest of the form. */
const TRIGGER = "w-full"

interface RecurrencePickerProps {
  value: RecurrenceState
  onChange: (next: RecurrenceState) => void
  /** The meeting's start date: the summary's "from", and the end date's floor. */
  startDate: Date
  /** A problem reported from outside, e.g. by submit. Shown under "End after". */
  error?: string
}

/**
 * Daily / weekly / monthly repeat rule for a scheduled meeting.
 *
 * Controlled: every change goes through `onChange` as a whole new state, and
 * the summary line is derived from the same state the form will save.
 */
export function RecurrencePicker({ value, onChange, startDate, error }: RecurrencePickerProps) {
  const { t, language } = useLanguage()
  const uid = useId()
  const id = (name: string) => `${uid}-${name}`

  const summary = useMemo(
    () => describeRecurrence(recurrenceToConfig(value), startDate, language, t),
    [value, startDate, language, t],
  )

  // Live, so a start date moved past the end date says so where it happened
  // rather than only on submit.
  const endProblem = recurrenceEndDateProblem(recurrenceToConfig(value), startDate)
  const endError = error ?? (endProblem ? t(END_DATE_PROBLEM_KEY[endProblem]) : undefined)

  const setModeInterval = (frequency: RecurringFrequency, interval: number) =>
    onChange({ ...value, intervals: { ...value.intervals, [frequency]: interval } })

  // No end date before the first meeting can be picked: an earlier one would
  // give a series with no meetings at all.
  const firstMeeting = firstMeetingDay(recurrenceToConfig(value), startDate)

  const setEndType = (endType: RecurrenceEndType) =>
    onChange({
      ...value,
      endType,
      // Start from the first meeting rather than an empty picker, so the
      // summary is complete the moment the option is chosen.
      endDate: endType === "date" ? (value.endDate ?? firstMeeting) : value.endDate,
    })

  const intervalSelect = (frequency: RecurringFrequency) => (
    <Select
      value={String(value.intervals[frequency])}
      onValueChange={(v) => setModeInterval(frequency, Number(v))}
    >
      <SelectTrigger id={id(`${frequency}-interval`)} className={TRIGGER}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {range(RECURRENCE_MAX_INTERVAL[frequency]).map((n) => (
          <SelectItem key={n} value={String(n)}>
            {plural(t, language, INTERVAL_KEY[frequency], n)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )

  const repeatEveryLabel = (frequency: RecurringFrequency) => (
    <Label htmlFor={id(`${frequency}-interval`)} className="text-sm font-semibold text-foreground">
      {t(`${KEY}.repeatEvery`)}
    </Label>
  )

  const onlyOneDay = value.weekDays.length === 1
  const countOn = value.endType === "count"
  const dateOn = value.endType === "date"

  return (
    <div className="@container space-y-4">
      <p role="status" aria-live="polite" className="text-sm font-semibold text-foreground">
        {summary}
      </p>

      <Tabs
        value={value.frequency}
        onValueChange={(v) => onChange(switchFrequency(value, v as RecurringFrequency, startDate))}
      >
        <TabsList
          aria-label={t(`${KEY}.frequencyLabel`)}
          className="grid w-full grid-cols-3 gap-2 h-auto p-0 bg-transparent rounded-none"
        >
          {RECURRENCE_FREQUENCIES.map((f) => (
            <TabsTrigger
              key={f}
              value={f}
              className={cn(
                // The outline button's look: a neutral border, blue only once chosen.
                "min-w-0 min-h-9 h-auto px-1 @sm:px-2 py-1.5 rounded-md border border-border data-[state=active]:border-primary",
                // "HEBDOMADAIRE" is wider than a third of a phone: hyphenate
                // (the page's lang is the UI language) rather than overflow.
                "whitespace-normal hyphens-auto [overflow-wrap:anywhere] text-center leading-tight",
                "text-xs font-semibold uppercase @sm:tracking-wide text-foreground",
                "hover:bg-card/40 dark:hover:bg-card/60 data-[state=active]:hover:bg-primary",
              )}
            >
              {t(FREQUENCY_LABEL[f])}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="daily" className="mt-4 space-y-2">
          {repeatEveryLabel("daily")}
          {intervalSelect("daily")}
        </TabsContent>

        <TabsContent value="weekly" className="mt-4 space-y-3">
          <div className="space-y-2">
            {repeatEveryLabel("weekly")}
            {intervalSelect("weekly")}
          </div>
          <fieldset>
            <legend className="sr-only">{t(`${KEY}.repeatOn`)}</legend>
            <p id={id("last-day")} className="sr-only">
              {t(`${KEY}.lastDayHint`)}
            </p>
            {/* Wraps rather than scrolls: seven boxes do not fit a phone. */}
            <div className="flex flex-wrap gap-x-4 gap-y-3">
              {WEEKDAY_KEYS.map((key, day) => {
                const checked = value.weekDays.includes(day)
                return (
                  <Checkbox
                    key={key}
                    id={id(`day-${day}`)}
                    label={t(`meetings.schedule.${key}`)}
                    checked={checked}
                    // The last day stays on; the hint says why.
                    aria-describedby={checked && onlyOneDay ? id("last-day") : undefined}
                    onCheckedChange={() => onChange(toggleWeekDay(value, day))}
                  />
                )
              })}
            </div>
          </fieldset>
        </TabsContent>

        <TabsContent value="monthly" className="mt-4 space-y-2">
          {repeatEveryLabel("monthly")}
          <div className="flex flex-col gap-2 @sm:flex-row @sm:items-center @sm:gap-3">
            <div className="min-w-0 @sm:flex-1">{intervalSelect("monthly")}</div>
            <Label htmlFor={id("month-day")} className="shrink-0 text-sm text-muted-foreground">
              {t(`${KEY}.on`)}
              <span className="sr-only"> ({t(`${KEY}.dayOfMonth`)})</span>
            </Label>
            <div className="min-w-0 @sm:w-28">
              <Select
                value={String(value.monthDay)}
                onValueChange={(v) => onChange({ ...value, monthDay: Number(v), monthDayChosen: true })}
              >
                <SelectTrigger id={id("month-day")} className={TRIGGER}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {range(31).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <div className="space-y-2">
        <Label id={id("end-label")} className="text-sm font-semibold text-foreground">
          {t("meetings.schedule.endAfter")}
        </Label>
        <RadioGroup
          value={value.endType}
          onValueChange={(v) => setEndType(v as RecurrenceEndType)}
          aria-labelledby={id("end-label")}
          className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @sm:gap-4 cursor-default"
        >
          <div className="flex min-w-0 items-center gap-3">
            <RadioGroupItem value="count" id={id("end-count")} aria-label={t(`${KEY}.endAfterCount`)} />
            <div className="min-w-0 flex-1">
              <Select
                value={String(value.occurrences)}
                onValueChange={(v) => onChange({ ...value, occurrences: Number(v) })}
                disabled={!countOn}
              >
                <SelectTrigger
                  id={id("end-count-value")}
                  aria-label={t(`${KEY}.occurrenceCount`)}
                  disabled={!countOn}
                  className={TRIGGER}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {range(RECURRENCE_MAX_OCCURRENCES).map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {plural(t, language, "meetingCount", n)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex min-w-0 items-center gap-3">
            <RadioGroupItem value="date" id={id("end-date")} aria-label={t(`${KEY}.endOnDate`)} />
            <div className="min-w-0 flex-1">
              <span id={id("end-date-label")} className="sr-only">
                {t(`${KEY}.endDate`)}
              </span>
              <DatePicker
                id={id("end-date-value")}
                aria-labelledby={`${id("end-date-label")} ${id("end-date-value")}`}
                aria-describedby={dateOn && endError ? id("end-error") : undefined}
                aria-invalid={dateOn && !!endError}
                // Shown greyed while inactive, as the date it would start from.
                date={value.endDate ?? firstMeeting}
                setDate={(d) => d && onChange({ ...value, endDate: startOfDay(d) })}
                minDate={firstMeeting}
                maxDate={latestEndDate(startDate)}
                disabled={!dateOn}
                format="PP"
                placeholder={t(`${KEY}.endDate`)}
              />
            </div>
          </div>
        </RadioGroup>
        {endError && (
          <p id={id("end-error")} className="text-xs text-error-600 dark:text-error-500">
            {endError}
          </p>
        )}
      </div>
    </div>
  )
}
